import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Bell, BellOff, CheckCheck } from 'lucide-react';
import { notificationService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { applyChange, useNotifications } from '../../contexts/NotificationsContext';
import { CATEGORY_LABEL, badgeCount, categoriesFor, groupByDate, timeLabel, typeMeta } from '../../utils/notifications';
import './Notifications.css';

const DROPDOWN_LIMIT = 15;

/** Sininho da barra superior: contagem de não lidas e as 15 mais recentes, com abas por tipo. */
export function NotificationBell() {
  const { user, isAdmin } = useAuth();
  const notif = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('todas');
  const [items, setItems] = useState(null);
  const ref = useRef(null);

  const total = notif?.counts.total || 0;
  const byCategory = notif?.counts.byCategory || {};

  const load = useCallback(async (category) => {
    try {
      const data = await notificationService.list({ category: category === 'todas' ? null : category, limit: DROPDOWN_LIMIT });
      setItems(data.items);
    } catch {
      setItems([]);
    }
  }, []);

  // Atualiza ao abrir e ao trocar de aba
  useEffect(() => {
    if (!open) return;
    setItems(null);
    load(tab);
    notif?.refresh();
  }, [open, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onChange = (e) => setItems(list => (list ? applyChange(list, e.detail) : list));
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('notifications:changed', onChange);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('notifications:changed', onChange);
    };
  }, [open]);

  if (!user || !notif) return null;

  const tabs = categoriesFor(isAdmin);
  const tabCount = (key) => (key === 'todas' ? total : byCategory[key] || 0);

  const openItem = (n) => {
    if (!n.read) notif.markRead(n, true);
    setOpen(false);
    if (n.link) navigate(n.link);
  };
  const toggle = (e, n) => {
    e.stopPropagation();
    notif.markRead(n, !n.read);
  };
  const seeAll = () => {
    setOpen(false);
    navigate(tab === 'todas' ? '/notificacoes' : `/notificacoes?tipo=${tab}`);
  };

  return (
    <div className="ntf-bell" ref={ref}>
      <button
        type="button"
        className={`ntf-bell__btn ${open ? 'is-open' : ''}`}
        onClick={() => setOpen(!open)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Notificações (${total} não lidas)`}
        data-tour="sininho"
      >
        <Bell size={18} strokeWidth={2.1} aria-hidden="true" />
        {total > 0 && <span className="ntf-bell__badge" aria-hidden="true">{badgeCount(total)}</span>}
      </button>

      {open && (
        <div className="ntf-drop" role="dialog" aria-label="Notificações">
          <div className="ntf-drop__head">
            <div className="ntf-drop__title">
              <h2>Notificações</h2>
              <span>{total === 1 ? '1 não lida' : `${total} não lidas`}</span>
            </div>
            {tabCount(tab) > 0 && (
              <button type="button" className="ntf-markall" onClick={() => notif.markAllRead(tab)}>
                <CheckCheck size={14} aria-hidden="true" /> Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="ntf-tabs" role="tablist" aria-label="Tipos">
            {tabs.map(t => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`ntf-tabs__item ${tab === t.key ? 'is-active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {tabCount(t.key) > 0 && <span className="ntf-tabs__count">{tabCount(t.key)}</span>}
              </button>
            ))}
          </div>

          <div className="ntf-drop__list">
            {items === null && <p className="ntf-drop__empty">Carregando...</p>}
            {items?.length === 0 && (
              <p className="ntf-drop__empty"><BellOff size={22} aria-hidden="true" /> Nada por aqui.</p>
            )}
            {items && groupByDate(items).map(g => (
              <div key={g.label}>
                <p className="ntf-drop__group">{g.label}</p>
                {g.items.map(n => {
                  const meta = typeMeta(n.type);
                  return (
                    <div
                      key={n.id}
                      role="button"
                      tabIndex={0}
                      className={`ntf-item ${n.read ? '' : 'is-unread'}`}
                      onClick={() => openItem(n)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openItem(n); } }}
                    >
                      <span className={`ntf-icon ntf-icon--${meta.tone}`}><meta.icon size={16} aria-hidden="true" /></span>
                      <span className="ntf-item__text">
                        <strong>{n.title}</strong>
                        {n.body && <span className="ntf-item__body">{n.body}</span>}
                        <span className="ntf-item__meta">{CATEGORY_LABEL[n.category]} · {timeLabel(n.createdAt)}</span>
                      </span>
                      <button
                        type="button"
                        className={`ntf-dot ${n.read ? '' : 'is-unread'}`}
                        onClick={(e) => toggle(e, n)}
                        title={n.read ? 'Marcar como não lida' : 'Marcar como lida'}
                        aria-label={n.read ? 'Marcar como não lida' : 'Marcar como lida'}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <button type="button" className="ntf-drop__foot" onClick={seeAll}>
            Ver todas as notificações <ArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
